import {invalidatePartialInterest} from 'resources/mandark.resource';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {addToast} from '@albert-io/atomic';
import {SchoolPersonnelInvitationModelV1} from 'resources/GeneratedModels/SchoolPersonnelInvitation/SchoolPersonnelInvitationModel.v1';

export function getPendingApprovedStaff(schoolId) {
  return resource('school_personnel_v1')
    .mandarkEndpoint(['school_personnel_v1'])
    .include('school_v5')
    .include('teacher_v1')
    .include('role_v1')
    .filter({
      school_v5: schoolId
    })
    .filter({
      status: 'pending'
    })
    .skipValidation({
      sort: true
    });
}

export function getPendingInvitations(schoolId) {
  return resource('schools_v5')
    .mandarkEndpoint(['schools_v5', schoolId])
    .include('school_personnel_invitations_v1')
    .filter({
      included: {
        school_personnel_invitations_v1: {
          accepted_at: {
            null: true
          }
        }
      }
    })
    .skipValidation({
      sort: true
    });
}

export function getPendingInviteCount(schoolId): Number {
  return getPendingInvitations(schoolId).isResourcePopulated()
    ? getPendingInvitations(schoolId).getResource().getSchoolPersonnelInvitations().size
    : 0;
}

export function getApprovedStaff(schoolId) {
  return resource('schools_v5')
    .mandarkEndpoint(['schools_v5', schoolId, 'school_personnel_v1'])
    .include('teacher_v1')
    .include('role_v1')
    .filter({
      all_of: [
        {
          status: 'approved'
        }
      ]
    })
    .skipValidation({
      sort: true
    });
}

export function getRejectedStaff(schoolId) {
  return resource('schools_v5')
    .mandarkEndpoint(['schools_v5', schoolId, 'school_personnel_v1'])
    .include('teacher_v1')
    .include('role_v1')
    .filter({
      all_of: [
        {
          status: 'rejected'
        }
      ]
    })
    .skipValidation({
      sort: true
    });
}

export async function approveUser({staff, schoolId}) {
  try {
    await staff.setStatus('approved').save();
    invalidatePartialInterest({
      resourcePath: ['schools_v5', schoolId, 'school_personnel_v1']
    });
    addToast({
      message: `${staff.getTeacher().getFullName()} was successfully approved.`,
      title: 'Success',
      type: 'success'
    });
  } catch (e) {
    addToast({
      message: `There was a problem approving ${staff.getTeacher().getFullName()}.`,
      title: 'Error',
      type: 'danger'
    });
  }
}

export async function rejectUser({staff, schoolId}) {
  try {
    await staff.setStatus('rejected').save();
    invalidatePartialInterest({
      resourcePath: ['schools_v5', schoolId, 'school_personnel_v1']
    });
    addToast({
      message: `${staff.getTeacher().getFullName()} was successfully rejected.`,
      title: 'Success',
      type: 'success'
    });
  } catch (e) {
    logger.error('Failed to reject school personnel: %s', e.message || 'unknown error');
    addToast({
      message: `There was a problem rejecting ${staff.getTeacher().getFullName()}.`,
      title: 'Error',
      type: 'danger'
    });
  }
}

export async function rejectInvite({invite, schoolId}) {
  try {
    await invite.delete();
    invalidatePartialInterest({
      resourcePath: ['schools_v5', schoolId, 'school_personnel_v1']
    });
    addToast({
      message: `${invite.getEmail()} was successfully deleted.`,
      title: 'Success',
      type: 'success'
    });
  } catch (e) {
    logger.error('Failed to delete school personnel invitation: %s', e.message || 'unknown error');
    addToast({
      message: `There was a problem deleting ${invite.getEmail()}.`,
      title: 'Error',
      type: 'danger'
    });
  }
}

export async function resendInvite(invite: SchoolPersonnelInvitationModelV1) {
  try {
    await invite.setSentEmailAt(null).save();
    addToast({
      message: `Resent invitation to ${invite.getEmail()}.`,
      title: 'Success',
      type: 'success'
    });
  } catch (e) {
    logger.error('Failed to re-send school personnel invitation: %s', e.message || 'unknown error');
    addToast({
      message: `There was a problem resending invitation to ${invite.getEmail()}.`,
      title: 'Error',
      type: 'danger'
    });
  }
}

export async function resendAll(schoolId) {
  try {
    const pendingInvites = await getPendingInvitations(schoolId).getResourcePromise();
    const pendingPersonnelInvitations = pendingInvites.getSchoolPersonnelInvitations();
    if (pendingPersonnelInvitations.size) {
      addToast({
        message: 'Resent invitations to all staff members.',
        title: 'Success',
        type: 'success'
      });
    } else {
      addToast({
        message: 'You have no pending invitations to send.',
        type: 'warning'
      });
    }
    await Promise.all(
      pendingPersonnelInvitations.map((staff) => staff.setSentEmailAt(null).save())
    );
  } catch (e) {
    logger.error(
      'Failed to re-send all school personnel invitations: %s',
      e.message || 'unknown error'
    );
    addToast({
      message: 'There was a problem resending invitations to all staff members.',
      title: 'Error',
      type: 'danger'
    });
  }
}

export async function approveAll(schoolId) {
  try {
    const pendingStaff = await getPendingApprovedStaff(schoolId).getResourcePromise();
    if (pendingStaff.size) {
      addToast({
        message: 'All staff members were successfully approved.',
        title: 'Success',
        type: 'success'
      });
    } else {
      addToast({
        message: 'You have no pending staff members to approve.',
        type: 'warning'
      });
    }
    await Promise.all(pendingStaff.map((staff) => staff.setStatus('approved').save()));
    invalidatePartialInterest({
      resourcePath: ['schools_v5', schoolId, 'school_personnel_v1']
    });
  } catch (e) {
    addToast({
      message: 'There was a problem approving all staff members.',
      title: 'Error',
      type: 'danger'
    });
  }
}

export function getPendingSearchFilters(query, term) {
  return query.filter({
    included: {
      school_personnel_v1: {
        any_of: [
          {
            teacher_v1: {
              first_name: {
                case_insensitive_substring: term
              }
            }
          },
          {
            teacher_v1: {
              last_name: {
                case_insensitive_substring: term
              }
            }
          },
          {
            teacher_v1: {
              email: {
                case_insensitive_substring: term
              }
            }
          }
        ]
      },
      school_personnel_invitations_v1: {
        email: {
          case_insensitive_substring: term
        }
      }
    }
  });
}

export function getNonPendingSearchFilters(query, term) {
  return query.filter({
    all_of: [
      {
        any_of: [
          {
            teacher_v1: {
              first_name: {
                case_insensitive_substring: term
              }
            }
          },
          {
            teacher_v1: {
              last_name: {
                case_insensitive_substring: term
              }
            }
          },
          {
            teacher_v1: {
              email: {
                case_insensitive_substring: term
              }
            }
          }
        ]
      }
    ]
  });
}
